import { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

const LoaderContext = createContext();

export const useLoader = () => {
    const context = useContext(LoaderContext);
    if (!context) throw new Error('useLoader must be used within a LoaderProvider');
    return context;
};

export const LoaderProvider = ({ children }) => {
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const showTimeRef = useRef(0);

    // Auto-hide loader when route changes, but only if loader was shown before this route
    useEffect(() => {
        if (loading && showTimeRef.current > 0) {
            setLoading(false);
            setMessage('');
            showTimeRef.current = 0;
        }
    }, [location.pathname]);

    const showLoader = useCallback((msg = '') => {
        setMessage(msg);
        setLoading(true);
        showTimeRef.current = Date.now();
    }, []);

    const hideLoader = useCallback(() => {
        setLoading(false);
        setMessage('');
        showTimeRef.current = 0;
    }, []);

    const value = useMemo(() => ({ showLoader, hideLoader }), [showLoader, hideLoader]);

    return (
        <LoaderContext.Provider value={value}>
            {children}
            {loading &&
                <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: '#fff' }}
                    role="alert" aria-live="assertive" aria-label={message || 'Loading'}>
                    <CircularProgress color="inherit" aria-hidden="true" />
                </Backdrop>
            }
        </LoaderContext.Provider>
    );
};
